// src/SWPCalculator.js
import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Register necessary components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const SWPCalculator = () => {
  const [investment, setInvestment] = useState(1500000);
  const [interestRate, setInterestRate] = useState(12.9);
  const [duration, setDuration] = useState(10);
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(21690);
  const [totalWithdrawal, setTotalWithdrawal] = useState(2602785);

  const calculateWithdrawal = () => {
    const monthlyRate = interestRate / 12 / 100;
    const months = duration * 12;
    const withdrawal = investment * monthlyRate / (1 - Math.pow(1 + monthlyRate, -months));
    setMonthlyWithdrawal(withdrawal.toFixed(2));
    setTotalWithdrawal((withdrawal * months).toFixed(2));
  };

  const handleInvestmentChange = (e) => {
    const value = e.target.value;
    setInvestment(value === '' ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === '' ? 0 : parseFloat(value));
  };

  const handleDurationChange = (e) => {
    const value = e.target.value;
    setDuration(value === '' ? 0 : parseInt(value));
  };

  const data = {
    labels: ['Total Investment', 'Monthly Withdrawal (Yearly)', 'Total Withdrawal'],
    datasets: [
      {
        label: 'SWP Breakdown',
        data: [investment, monthlyWithdrawal * 12, totalWithdrawal - investment],
        backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
      },
    ],
  };
 function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is an SWP in mutual funds?',
        answer:
          'An SWP or Systematic Withdrawal Plan allows you to withdraw a fixed amount regularly from your mutual fund investment.',
      },
      {
        question: 'How does an SWP work?',
        answer:
          'A set amount is withdrawn at regular intervals from your total invested corpus while the remaining amount continues to earn returns.',
      },
      {
        question: 'Can I start an SWP with any mutual fund?',
        answer:
          'Most mutual funds allow SWPs, especially open-ended schemes. Always confirm with the specific AMC.',
      },
      {
        question: 'Who should use an SWP?',
        answer:
          'SWPs are ideal for retirees, freelancers, and anyone needing steady income from investments.',
      },
      {
        question: 'Is the SWP amount fixed?',
        answer:
          'Yes, you decide the fixed amount to withdraw, but it can be changed or stopped anytime.',
      },
      {
        question: 'How is an SWP different from an SIP?',
        answer:
          'SIP is used for systematic investment, while SWP is for systematic withdrawal.',
      },
      {
        question: 'Is the withdrawn amount taxable?',
        answer:
          'Yes, capital gains tax is applicable depending on fund type and holding period.',
      },
      {
        question: 'What happens if my fund underperforms?',
        answer:
          'Your corpus may reduce faster, so review and adjust your SWP periodically.',
      },
      {
        question: 'Can I stop an SWP midway?',
        answer:
          'Yes, you can pause or stop SWPs anytime without penalty.',
      },
      {
        question: 'Is there a minimum amount to start SWP?',
        answer:
          'It varies by fund house but usually starts at Rs. 500 or Rs. 1,000 per withdrawal.',
      },
      {
        question: 'Can I withdraw quarterly or annually instead of monthly?',
        answer:
          'Yes, SWPs can be set monthly, quarterly, semi-annually, or annually.',
      },
      {
        question: 'What happens to the remaining investment?',
        answer:
          'It remains invested in the fund and continues to earn returns.',
      },
      {
        question: 'Can I use SWP in equity mutual funds?',
        answer:
          'Yes, though equity funds are market-linked and may have higher volatility.',
      },
      {
        question: 'Are there any charges for setting up SWP?',
        answer:
          'Generally, there are no charges, but always check with your fund house.',
      },
      {
        question: 'Can I use SWP to plan retirement?',
        answer:
          'Absolutely. SWPs are widely used for post-retirement income planning.',
      },
    ];
    
    
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
     <Helmet>
         <title>Systematic Withdrawal Plan Calculator- SWP Calculator Online 2024 | TheFinmaster</title>
        <meta  name='description' content='SWP Calculator - Systematic Withdrawal Plan calculator considers the returns generated by your investment to determine the total value of your investment when your SWP tenure ends. | TheFinmaster'/>
        <meta name='keywords' content='systematic withdrawal plan calculator,swp calculator,best swp calculator,swp mutual fund calculator,sbi systematic withdrawal plan calculator,systematic withdrawal plan calculator with inflation' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/swp-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">SWP Calculator</span>
        </li> 
      
      </ol>
    </nav>
    <div className=' py-5'>
    <div className="container mx-auto p-4 shadow-lg rounded-md">
      <h1 className="text-2xl font-bold mb-4">SWP Calculator Online: Understand Systematic Withdrawal Plan</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Total Investment</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="10000000"
                value={investment}
                onChange={handleInvestmentChange}
                className="w-1/2 p-2 border rounded mr-2"
                
              />
              <span className="text-gray-700">₹ {investment.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000000"
              value={investment}
              onChange={(e) => setInvestment(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Interest Rate</label>
            <div className="flex items-center">
              <input
                type="number"
                min="5"
                max="15"
                step="0.1"
                value={interestRate}
                onChange={handleInterestRateChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{interestRate} %</span>
            </div>
            <input
              type="range"
              min="5"
              max="15"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Duration</label>
            <div className="flex items-center">
              <input
                type="number"
                min="1"
                max="25"
                value={duration}
                onChange={handleDurationChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{duration} Yr</span>
            </div>
            <input
              type="range"
              min="1"
              max="25"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <button
            onClick={calculateWithdrawal}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4 lg:ml-4 sm:mt-0">
            <h2 className="text-xl font-bold ">Results</h2>
            <p>Total Investment: ₹ {investment.toLocaleString()}</p>
            <p>Monthly Withdrawal: ₹ {monthlyWithdrawal}</p>
            <p>Total Withdrawal: ₹ {totalWithdrawal.toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold lg:ml-4 sm:mt-0">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
        {/* <div className="w-full md:w-2/3 p-4">
          <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">SWP (Systematic Withdrawal Plan) </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg  mt-2">SWP stands for Systematic Withdrawal Plan. Under SWP, if you invest LumpSum in a mutual fund, you can set an amount you’ll withdraw regularly and the frequency at which you’ll withdraw.</p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg  mt-2">For example, let’s say you invested in HDFC Top 200 Fund an amount of ₹1 lakh for a year. Let’s assume that you decided to withdraw an amount of ₹10000 per month. So every month, your investment in the fund will reduce by ₹10000. The amount left every month after withdrawal will continue to remain invested.</p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg  mt-2">Use the above SWP calculator to know how much you can withdraw from your lumpsum investments.</p>

          <div className="container mx-auto  py-4">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">How can a SWP calculator assist you?</h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
            As per the Systematic Withdrawal Plan, an individual needs to invest a particular amount and withdraw a certain amount of the corpus invested each month. After withdrawal, the amount will be deducted from the investment while it continues to accumulate interest.            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
            It is difficult to accurately calculate the monthly withdrawals and total matured sum. TheFinMaster can help you with this complex calculations with the SWP calculator.The Systematic Withdrawal Plan calculator accurately computes your matured sum based on your monthly withdrawals. Calculators are simplifying the way people invest in mutual funds.              </p>
          
          </div>
          <div className="container mx-auto  py-4">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">Example of Systematic Withdrawal Plan</h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
            As mentioned before, the SWP allows investors to generate both monthly revenue as well as an accumulated sum at the end of the maturity period. Refer to this investment and withdrawal schedule for an in-depth idea.
            </p>          
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Here, an individual has invested Rs. 50,000 for tenure of 1 year along with a systematic withdrawal of Rs. 1,000 per month. Interest rate stands at 10%.
          </p>
          <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
          As such, total return of investments after the end of the  tenure stands at Rs. 4,565.          </p>
          <div className="mx-auto max-w-4xl mt-4">
            
    <div className="overflow-x-auto">
        <table className="table-auto w-full border-collapse border border-gray-300">
            <thead className="bg-gray-800 text-white">
                <tr>
                <th className="px-4 py-2 text-left">Months</th>
                <th className="px-4 py-2 text-left">Opening Balance</th>
                <th className="px-4 py-2 text-left">Withdrawals</th>
                <th className="px-4 py-2 text-left">Interest Earned</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-300 text-gray-600">                                        
            <tr>
                <td className="px-4 py-2">1</td>
                <td className="px-4 py-2"> Rs. 50,000</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 408</td>
            </tr>
            <tr >
                <td className="px-4 py-2">2</td>
                <td className="px-4 py-2">Rs. 49,408</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 403</td>
            </tr>
            <tr>
                <td className="px-4 py-2">3</td>
                <td className="px-4 py-2">Rs. 48,812</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 398</td>
            </tr>
            <tr>
                <td className="px-4 py-2">4</td>
                <td className="px-4 py-2">Rs. 48,210</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 393</td>
            </tr>
            <tr>
                <td className="px-4 py-2">5</td>
                <td className="px-4 py-2">Rs. 47,604</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 388</td>
            </tr>
            <tr>
                <td className="px-4 py-2">6</td>
                <td className="px-4 py-2">Rs. 47,604</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 388</td>
            </tr>
            <tr>
                <td className="px-4 py-2">7</td>
                <td className="px-4 py-2">Rs. 46,922</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 383</td>
            </tr>
            <tr>
                <td className="px-4 py-2">8</td>
                <td className="px-4 py-2">Rs. 46,375</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 378</td>
            </tr>
            <tr>
                <td className="px-4 py-2">9</td>
                <td className="px-4 py-2">Rs. 45,753</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 373</td>
            </tr>
            <tr>
                <td className="px-4 py-2">10</td>
                <td className="px-4 py-2">Rs. 45,126</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 368</td>
            </tr>
            <tr>
                <td className="px-4 py-2">11</td>
                <td className="px-4 py-2">Rs. 44,494</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 362</td>
            </tr>
            <tr>
                <td className="px-4 py-2">12</td>
                <td className="px-4 py-2">Rs. 43,856</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 357</td>
            </tr>
            <tr>
                <td className="px-4 py-2">13</td>
                <td className="px-4 py-2">Rs. 43,214</td>
                <td className="px-4 py-2">Rs. 1,000</td>
                <td className="px-4 py-2">Rs. 352</td>
            </tr>
               
            </tbody>
        </table>
    </div>
</div>

          </div>
          
          <div className="container mx-auto  py-4">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">How to use SWP calculator?</h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
            The calculator is user-friendly and easy to use first time out. However, you need to ensure that all values are properly put a.             </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2"> 
              <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> 
              You will find three columns namely tenure, expected return and amount invested. Fill in the values as per your investment plan.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 "> 
              <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> 
              Enter values and calculate to find a similar SWP returns chart as mentioned above.
            </p>
                      </div>

          <div className="container mx-auto  py-2">

            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 mt-2">How can a SWP calculator benefit you?</h1>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">Retirees and senior citizens are among the most common investors in this scheme. Such individuals obviously require a fixed monthly financial input. An SWP can provide the same over and above the pension or even as an alternative. They can easily use TheFinMaster simple and intuitive systematic withdrawal plan calculator for numerous benefits.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2"> 
              <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> 
              A Systematic Withdrawal Plan ensures monthly returns while generating RoI on the total investment. This calculator can Easily help compute both accurately.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> 
              <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> 
              It’s easy to set the amount which you wish to withdraw every month and calculate amount on maturity accordingly.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> 
              It is easily accessible online.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> 
              <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> 
              This SWP return calculator does not require any expertise to operate. Users need to use insert instead of this variables in proper space and output is ready in no time.
            </p>

          </div>
          <h1 className="text-xl font-medium  text-gray-700  ">“Looking to invest? Open an account with TheFinMaster and start investing in direct Mutual Funds for free”</h1>




    

         

        
       
        </div> */}
<div className="w-full md:w-2/3 p-4">
  <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">SWP (Systematic Withdrawal Plan) Calculator: Your Gateway to Smart Withdrawals</h1>
  <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">A Systematic Withdrawal Plan (SWP) allows mutual fund investors to withdraw a fixed amount of money at regular intervals from their lump sum investments. This not only offers liquidity but also provides a consistent income stream while the remaining investment continues to grow.</p>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">What is SWP? A Smarter Way to Access Your Money</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">SWP stands for Systematic Withdrawal Plan. It is a withdrawal strategy where investors redeem a fixed amount from their mutual fund investments at set intervals. Ideal for retirees or those seeking a fixed income stream, SWP ensures your money remains invested while you draw income periodically.</p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">For example, suppose you invested ₹1 lakh in the HDFC Top 200 Fund. You set an SWP to withdraw ₹10,000 every month. Each month, ₹10,000 is withdrawn, and the remaining corpus continues to earn returns based on the fund's performance.</p>
  </div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">Why Choose an SWP? Key Benefits for Investors</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Regular Income Stream : Perfect for meeting monthly expenses or lifestyle needs.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Customizable Withdrawals : Choose your own withdrawal amount and frequency (monthly, quarterly, etc.).
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Tax Efficiency : Reduces tax liability over time compared to lump sum withdrawals.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Continued Growth : Remaining investment continues to grow, ensuring wealth creation.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Flexibility : SWPs can be stopped or altered at any time based on your financial needs.
    </p>
  </div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">How an SWP Calculator Helps You Plan Smartly</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">Planning your withdrawals manually is tricky due to multiple variables like interest rate, investment tenure, and compounding frequency. The SWP calculator from TheFinMaster makes this process simple:</p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Accurate Estimations : Computes your returns and remaining corpus after periodic withdrawals.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Ease of Use : Just input investment amount, frequency, and expected returns.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Investment Planning : Helps you visualize how long your investment will last with your set withdrawal amount.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Custom Scenarios : Easily modify inputs to explore different investment or withdrawal strategies.
    </p>
  </div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">Example of Systematic Withdrawal Plan</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">The SWP allows investors to generate both monthly revenue as well as an accumulated sum at the end of the maturity period. Refer to this investment and withdrawal schedule for an in-depth idea.</p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">Here, an individual has invested ₹50,000 for a tenure of 1 year along with a systematic withdrawal of ₹1,000 per month. The interest rate stands at 10%.</p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">As such, the total return on investments after the end of the tenure stands at ₹4,565.</p>

    {/* <div className="mx-auto max-w-4xl mt-4">
      <div className="overflow-x-auto">
        <table className="table-auto w-full border-collapse border border-gray-300 md:table">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="px-2 py-2 text-left text-sm md:text-base md:px-4">Months</th>
              <th className="px-2 py-2 text-left text-sm md:text-base md:px-4">Opening Balance</th>
              <th className="px-2 py-2 text-left text-sm md:text-base md:px-4">Withdrawals</th>
              <th className="px-2 py-2 text-left text-sm md:text-base md:px-4">Interest Earned</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-300 text-gray-600">
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">1</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹50,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹408</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">2</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹49,408</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹403</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">3</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹48,811</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹398</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">4</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹48,209</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹393</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">5</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹47,602</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹388</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">6</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹46,990</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹383</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">7</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹46,373</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹378</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">8</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹45,751</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹373</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">9</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹45,124</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹368</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">10</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹44,492</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹362</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">11</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹43,854</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹357</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">12</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹43,211</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹352</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Month:'] before:font-bold before:mr-2 md:before:content-none">13</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Opening_Balance:'] before:font-bold before:mr-2 md:before:content-none">₹47,604</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Withdrawals:'] before:font-bold before:mr-2 md:before:content-none">₹1,000</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Interest_Earned:'] before:font-bold before:mr-2 md:before:content-none">₹388</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div> */}
    <div className="mx-auto max-w-4xl mt-4">
  <div className="overflow-x-auto">
    <table className="w-full border border-gray-300 hidden md:table">
      <thead className="bg-gray-800 text-white">
        <tr>
          <th className="px-4 py-2 text-left">Months</th>
          <th className="px-4 py-2 text-left">Opening Balance</th>
          <th className="px-4 py-2 text-left">Withdrawals</th>
          <th className="px-4 py-2 text-left">Interest Earned</th>
        </tr>
      </thead>
      <tbody className="text-gray-700">
        {[
          { month: 1, opening: "₹50,000", withdraw: "₹1,000", interest: "₹408" },
          { month: 2, opening: "₹49,408", withdraw: "₹1,000", interest: "₹403" },
          { month: 3, opening: "₹48,811", withdraw: "₹1,000", interest: "₹398" },
          { month: 4, opening: "₹48,209", withdraw: "₹1,000", interest: "₹393" },
          { month: 5, opening: "₹47,602", withdraw: "₹1,000", interest: "₹388" },
          { month: 6, opening: "₹46,990", withdraw: "₹1,000", interest: "₹383" },
          { month: 7, opening: "₹46,373", withdraw: "₹1,000", interest: "₹378" },
          { month: 8, opening: "₹45,751", withdraw: "₹1,000", interest: "₹373" },
          { month: 9, opening: "₹45,124", withdraw: "₹1,000", interest: "₹368" },
          { month: 10, opening: "₹44,492", withdraw: "₹1,000", interest: "₹363" },
          { month: 11, opening: "₹43,855", withdraw: "₹1,000", interest: "₹358" },
          { month: 12, opening: "₹43,213", withdraw: "₹1,000", interest: "₹353" },
        ].map((row, index) => (
          <tr key={index}>
            <td className="border px-4 py-2">{row.month}</td>
            <td className="border px-4 py-2">{row.opening}</td>
            <td className="border px-4 py-2">{row.withdraw}</td>
            <td className="border px-4 py-2">{row.interest}</td>
          </tr>
        ))}
      </tbody>
    </table>

    {/* Mobile stacked layout */}
    <div className="md:hidden space-y-4">
      {[
        { month: 1, opening: "₹50,000", withdraw: "₹1,000", interest: "₹408" },
        { month: 2, opening: "₹49,408", withdraw: "₹1,000", interest: "₹403" },
        { month: 3, opening: "₹48,811", withdraw: "₹1,000", interest: "₹398" },
        { month: 4, opening: "₹48,209", withdraw: "₹1,000", interest: "₹393" },
        { month: 5, opening: "₹47,602", withdraw: "₹1,000", interest: "₹388" },
        { month: 6, opening: "₹46,990", withdraw: "₹1,000", interest: "₹383" },
        { month: 7, opening: "₹46,373", withdraw: "₹1,000", interest: "₹378" },
        { month: 8, opening: "₹45,751", withdraw: "₹1,000", interest: "₹373" },
        { month: 9, opening: "₹45,124", withdraw: "₹1,000", interest: "₹368" },
        { month: 10, opening: "₹44,492", withdraw: "₹1,000", interest: "₹363" },
        { month: 11, opening: "₹43,855", withdraw: "₹1,000", interest: "₹358" },
        { month: 12, opening: "₹43,213", withdraw: "₹1,000", interest: "₹353" },
      ].map((row, index) => (
        <div key={index} className="border border-gray-300 p-4 rounded-md">
          <p><span className="font-semibold">Month:</span> {row.month}</p>
          <p><span className="font-semibold">Opening Balance:</span> {row.opening}</p>
          <p><span className="font-semibold">Withdrawals:</span> {row.withdraw}</p>
          <p><span className="font-semibold">Interest Earned:</span> {row.interest}</p>
        </div>
      ))}
    </div>
  </div>
</div>

  </div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">How to Use TheFinMaster SWP Calculator</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">Using the calculator is straightforward:</p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Enter Investment Amount: Input the lump sum amount you’ve invested.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Select Frequency: Monthly, quarterly, or annual withdrawals.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Expected Return: Provide the expected annual return rate.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Withdrawal Amount: Input the amount you want to withdraw periodically.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Tenure: Define the time period for investment.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Click Calculate: Instantly see your output, including total returns and remaining investment.
    </p>
  </div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">Advantages of an SWP over Traditional Withdrawals</h1>
    <div className="mx-auto max-w-4xl mt-4">
      <div className="overflow-x-auto">
        <table className="table-auto w-full border-collapse border border-gray-300 md:table">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="px-2 py-2 text-left text-sm md:text-base md:px-4">Feature</th>
              <th className="px-2 py-2 text-left text-sm md:text-base md:px-4">SWP</th>
              <th className="px-2 py-2 text-left text-sm md:text-base md:px-4">Traditional Lump Sum Withdrawal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-300 text-gray-600">
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Feature:'] before:font-bold before:mr-2 md:before:content-none">Regular Income</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SWP:'] before:font-bold before:mr-2 md:before:content-none">✅ Yes</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Traditional_Lump_Sum_Withdrawal:'] before:font-bold before:mr-2 md:before:content-none">❌ No</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Feature:'] before:font-bold before:mr-2 md:before:content-none">Corpus Longevity</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SWP:'] before:font-bold before:mr-2 md:before:content-none">✅ Longer Due to Growth</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Traditional_Lump_Sum_Withdrawal:'] before:font-bold before:mr-2 md:before:content-none">❌ Exhausted Quickly</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Feature:'] before:font-bold before:mr-2 md:before:content-none">Tax Efficiency</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SWP:'] before:font-bold before:mr-2 md:before:content-none">✅ Yes (Capital Gains)</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Traditional_Lump_Sum_Withdrawal:'] before:font-bold before:mr-2 md:before:content-none">❌ May incur higher tax burden</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Feature:'] before:font-bold before:mr-2 md:before:content-none">Market Exposure Retained</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SWP:'] before:font-bold before:mr-2 md:before:content-none">✅ Yes</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Traditional_Lump_Sum_Withdrawal:'] before:font-bold before:mr-2 md:before:content-none">❌ No</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Feature:'] before:font-bold before:mr-2 md:before:content-none">Ideal for Retirement Planning</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SWP:'] before:font-bold before:mr-2 md:before:content-none">✅ Perfect Fit</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Traditional_Lump_Sum_Withdrawal:'] before:font-bold before:mr-2 md:before:content-none">❌ Not Sustainable</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">Things to Consider Before Starting an SWP</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Fund Type: Choose between equity, hybrid, or debt funds based on your risk appetite.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Market Volatility: SWPs from equity funds may vary in effectiveness during bearish markets.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Withdrawal Amount: Should be within the expected returns to avoid capital erosion.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className ="absolute left-0 top-0 text-blue-600">•</span>
      Tax Implications: Short-term and long-term capital gains taxes apply.
    </p>
  </div>
{/* 
  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">How SWP Differs from SIP?</h1>
    <div className="mx-auto max-w-4xl mt-4">
      <div className="overflow-x-auto">
        <table className="table-auto w-full border-collapse border border-gray-300 md:table">
          <thead className="divide-y bg-gray-800 text-white">
            <tr>
              <th className="px-2 py-2 text-left text-sm md:text-base md:px-4">Factor</th>
              <th className="px-2 py-2 text-left text-sm md:text-base md:px-4">SWP</th>
              <th className="px-2 py-2 text-left text-sm md:text-base md:px-4">SIP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-300 text-gray-600">
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Factor:'] before:font-bold before:mr-2 md:before:content-none">Purpose</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SWP:'] before:font-bold before:mr-2 md:before:content-none">Withdrawal of funds</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SIP:'] before:font-bold before:mr-2 md:before:content-none">Investment into funds</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Factor:'] before:font-bold before:mr-2 md:before:content-none">Ideal For</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SWP:'] before:font-bold before:mr-2 md:before:content-none">Retirees or income seekers</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SIP:'] before:font-bold before:mr-2 md:before:content-none">Working individuals or savers</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Factor:'] before:font-bold before:mr-2 md:before:content-none">Cash Flow Direction</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SWP:'] before:font-bold before:mr-2 md:before:content-none">Outflow (you receive money)</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SIP:'] before:font-bold before:mr-2 md:before:content-none">Inflow (you invest money)</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Factor:'] before:font-bold before:mr-2 md:before:content-none">Flexibility</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SWP:'] before:font-bold before:mr-2 md:before:content-none">High (withdrawal amount control)</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SIP:'] before:font-bold before:mr-2 md:before:content-none">High (amount and frequency control)</td>
            </tr>
            <tr className="block md:table-row">
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['Factor:'] before:font-bold before:mr-2 md:before:content-none">Tax Treatment</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SWP:'] before:font-bold before:mr-2 md:before:content-none">On capital gains</td>
              <td className="block px-2 py-2 text-sm md:text-base md:table-cell md:px-4 before:content-['SIP:'] before:font-bold before:mr-2 md:before:content-none">On capital gains</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div> */}
<div className="container mx-auto px-4 py-4">
  <h1 className="text-base font-semibold bg-blue-100 border-l-4 border-blue-600 pl-3 py-2">
    How SWP Differs from SIP?
  </h1>
  <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
    Understand the key differences between SWP (Systematic Withdrawal Plan) and SIP (Systematic Investment Plan).
  </p>

  <div className="max-w-4xl mx-auto overflow-hidden rounded-lg border border-gray-300 shadow">
    {/* Header */}
    <div className="hidden md:grid grid-cols-3 bg-gray-800 text-white text-sm font-semibold text-center">
      <div className="px-3 py-2 border-r">Factor</div>
      <div className="px-3 py-2 border-r">SWP</div>
      <div className="px-3 py-2">SIP</div>
    </div>

    {/* Data rows for mobile (stacked) and desktop (grid) */}
    <div className="divide-y divide-gray-200 text-sm md:text-base text-gray-600 ">
      {/* Row Template */}
      {[
        {
          factor: "Purpose",
          swp: "Withdrawal of funds",
          sip: "Investment into funds",
        },
        {
          factor: "Ideal For",
          swp: "Retirees or income seekers",
          sip: "Working individuals or savers",
        },
        {
          factor: "Cash Flow Direction",
          swp: "Outflow (you receive money)",
          sip: "Inflow (you invest money)",
        },
        {
          factor: "Flexibility",
          swp: "High (withdrawal amount control)",
          sip: "High (amount and frequency control)",
        },
        {
          factor: "Tax Treatment",
          swp: "On capital gains",
          sip: "On capital gains",
        },
      ].map((row, idx) => (
        <div
          key={idx}
          className="block md:grid md:grid-cols-3 hover:bg-gray-50 transition"
        >
          {/* Mobile version */}
          <div className="block md:hidden px-4 py-3">
            <p className="font-semibold">Factor: <span className="font-normal">{row.factor}</span></p>
            <p className="font-semibold">SWP: <span className="font-normal">{row.swp}</span></p>
            <p className="font-semibold">SIP: <span className="font-normal">{row.sip}</span></p>
          </div>

          {/* Desktop version */}
          <div className="hidden md:block px-4 py-3 border-r">{row.factor}</div>
          <div className="hidden md:block px-4 py-3 border-r">{row.swp}</div>
          <div className="hidden md:block px-4 py-3">{row.sip}</div>
        </div>
      ))}
    </div>
  </div>
</div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">Tax Implications on SWP</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">When you withdraw money under SWP, it’s considered a redemption of mutual fund units. The taxation depends on:</p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2"><strong>Equity Funds:</strong></p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      LTCG (after 1 year): Taxed at 10% (above ₹1 lakh gains).
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      STCG (within 1 year): Taxed at 15%.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2"><strong>Debt Funds:</strong></p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Taxed as per your income slab (effective from April 2023—no indexation benefit for LTCG).
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">Each withdrawal under SWP is part return of capital and part capital gain.</p>
  </div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">Why Choose TheFinMaster’s SWP Calculator?</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Designed for both beginners and advanced investors.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Mobile and desktop responsive for ease of use anywhere.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Accurate financial projections backed by real-time compounding logic.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Simplified UI with powerful backend calculations.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Developed by a team of investment professionals to align with investor needs.
    </p>
  </div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">Who Should Use an SWP Calculator?</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Retirees : Looking for a fixed monthly income to supplement pension.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Households : Planning systematic withdrawals for tuition, EMIs, or recurring bills.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      High Net-Worth Individuals (HNIs) : Wanting tax-efficient wealth drawdown plans.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Freelancers : Seeking periodic cash flow without disturbing long-term investments.
    </p>
  </div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">TheFinMaster SWP Calculator Advantages</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Simplified Financial Planning : No need to manually calculate complex figures.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Real-Time Visualization : Understand how long your investments can support your withdrawals.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Mobile-Friendly : Accessible anytime from desktop or phone.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Goal-Oriented : Plan SWPs for education, vacation, home loan repayments, or retirement.
    </p>
  </div>

  <div className="container mx-auto py-4">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">Tips for Effective SWP Planning</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Avoid withdrawing more than your fund’s average returns.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Review your plan annually and adjust based on fund performance.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Combine with SIPs for balanced investing and withdrawal strategy.
    </p>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2 relative pl-6">
      <span className="absolute left-0 top-0 text-blue-600">•</span>
      Consider funds with consistent historical performance.
    </p>
  </div>

  <div className="container mx-auto py-2">
    <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">Start Your Investment Journey Today</h1>
    <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">SWP is a smart way to turn your mutual fund investment into a regular source of income while still earning returns. Whether you’re planning for retirement or budgeting for future expenses, TheFinMaster SWP Calculator can help you stay financially stable.</p>
    <h1 className="text-xl font-medium text-gray-700 mt-4">“Looking to invest? Open an account with TheFinMaster and start investing in direct Mutual Funds for free”</h1>
  </div>
</div>
        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
            <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>

        </div>

      </div>

         {/* FAQ section */}
    <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default SWPCalculator;
